import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

import { API_BASE_URL as API } from '../utils/apiConfig';

const ChatContext = createContext({
  threads: [],
  currentThread: null,
  messages: [],
  selectedBrand: null,
  brands: [],
  isLoading: false,
  isStreaming: false,
  setSelectedBrand: () => {},
  selectBrand: () => {},
  loadBrands: () => {},
  loadThreads: () => {},
  createThread: () => {},
  selectThread: () => {},
  deleteThread: () => {},
  renameThread: () => {},
  sendMessage: () => {},
  stopGeneration: () => {},
});

export function ChatProvider({ children }) {
  const [threads, setThreads] = useState([]);
  const [currentThread, setCurrentThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [abortController, setAbortController] = useState(null);

  const loadBrands = useCallback(async (retryCount = 0) => {
    try {
      console.log('Loading brands from:', `${API}/api/brands`);
      const response = await axios.get(`${API}/api/brands`, {
        withCredentials: true,
        timeout: 30000  // 30s — `/api/brands` does per-brand stat aggregation; allow generous window
      });
      console.log('Brands response:', response.data);
      const allBrands = response.data.brands || [];
      
      // Deduplicate brands by brand_id (safety check)
      const uniqueBrands = allBrands.reduce((acc, brand) => {
        const exists = acc.find(b => String(b.brand_id) === String(brand.brand_id));
        if (!exists) {
          acc.push(brand);
        }
        return acc;
      }, []);
      
      setBrands(uniqueBrands);
      console.log('Unique brands set:', uniqueBrands.length);
      
      // Auto-select a brand if none is selected
      if (uniqueBrands.length > 0) {
        // Check if user is a brand admin (not super admin) - force select their brand
        const userBrandId = localStorage.getItem('userBrandId');
        const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
        
        if (userBrandId && !isSuperAdmin) {
          const userBrand = uniqueBrands.find(b => String(b.brand_id) === userBrandId);
          if (userBrand) {
            console.log('Auto-selecting user brand:', userBrand.name);
            setSelectedBrand(userBrand);
            localStorage.setItem('selectedBrandId', String(userBrand.brand_id));
            return;
          }
        }
        
        // For Super Admin - DON'T auto-select, show landing page instead
        if (isSuperAdmin) {
          // Check if there's a saved brand selection in localStorage
          const savedBrandId = localStorage.getItem('selectedBrandId');
          if (savedBrandId) {
            const savedBrand = uniqueBrands.find(b => String(b.brand_id) === savedBrandId);
            if (savedBrand) {
              console.log('Restoring saved brand for super admin:', savedBrand.name);
              setSelectedBrand(savedBrand);
              return;
            }
          }
          // No saved brand for super admin - leave selectedBrand as null
          console.log('Super Admin logged in - no default brand, showing landing page');
          setSelectedBrand(null);
          return;
        }
        
        // For non-super admin - check saved brand or auto-select first brand
        const savedBrandId = localStorage.getItem('selectedBrandId');
        if (savedBrandId) {
          const savedBrand = uniqueBrands.find(b => String(b.brand_id) === savedBrandId);
          if (savedBrand) {
            console.log('Restoring saved brand:', savedBrand.name);
            setSelectedBrand(savedBrand);
            return;
          }
        }
        
        // No saved brand - auto-select first brand with data
        const brandsWithData = uniqueBrands.filter(b => b.db_brand_id || b.settings?.db_brand_id);
        const brandToSelect = brandsWithData.length > 0 ? brandsWithData[0] : uniqueBrands[0];
        console.log('Auto-selecting first brand:', brandToSelect.name);
        setSelectedBrand(brandToSelect);
        localStorage.setItem('selectedBrandId', String(brandToSelect.brand_id));
      }
    } catch (error) {
      // Ignore navigation aborts / network errors — they're not real failures.
      // Common signatures: axios CanceledError (cancel token), ERR_CANCELED,
      // ERR_NETWORK on a route-change-aborted XHR, AbortError from fetch.
      const isAbortOrNet = (
        error?.code === 'ERR_CANCELED' ||
        error?.code === 'ERR_NETWORK' ||
        error?.message?.toLowerCase?.().includes('canceled') ||
        error?.message?.toLowerCase?.().includes('aborted') ||
        error?.message?.toLowerCase?.().includes('network error') ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError'
      );
      if (isAbortOrNet) {
        console.warn('Brands fetch was aborted/networked-out (navigation race) — keeping previously loaded brands.');
        return;  // Do NOT reset brands; keep whatever was already loaded.
      }
      // Real auth or 5xx error — only clear if we actually got a server-level rejection.
      const status = error?.response?.status;
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.toLowerCase?.().includes('timeout');
      console.error('Failed to load brands (status=' + status + ', timeout=' + isTimeout + '):', error?.message || error);
      // Retry-once on timeout — the brand stats endpoint can be slow on cold start.
      if (isTimeout && retryCount < 2) {
        console.warn(`Retrying loadBrands (attempt ${retryCount + 1}/2) after timeout...`);
        await new Promise(r => setTimeout(r, 1500));
        return loadBrands(retryCount + 1);
      }
      // Only wipe brands on a clear rejection; otherwise keep stale data so UI doesn't blank.
      if (status && status >= 400 && status < 500 && status !== 401) {
        setBrands([]);
      }
    }
  }, []);

  const selectBrand = useCallback((brand) => {
    setSelectedBrand(brand);
    setCurrentThread(null);
    setMessages([]);
    // Persist selected brand to localStorage
    if (brand) {
      localStorage.setItem('selectedBrandId', brand.brand_id?.toString());
    }
  }, []);

  const loadThreads = useCallback(async (brandId) => {
    if (!brandId) return;
    try {
      const response = await axios.get(`${API}/api/threads?brand_id=${brandId}`, {
        withCredentials: true,
      });
      setThreads(response.data.threads || []);
    } catch (error) {
      console.error('Failed to load threads:', error);
    }
  }, []);

  const createThread = useCallback(async (brandId = null, initialMessage = null) => {
    const useBrandId = brandId || selectedBrand?.brand_id;
    if (!useBrandId) return null;
    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/api/threads`, {
        brand_id: String(useBrandId),
        title: initialMessage ? initialMessage.substring(0, 50) : 'New Chat',
      }, {
        withCredentials: true,
      });
      const newThread = response.data;
      setThreads((prev) => [newThread, ...prev]);
      setCurrentThread(newThread);
      setMessages([]);
      
      return { thread: newThread, initialMessage };
    } catch (error) {
      console.error('Failed to create thread:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [selectedBrand]);

  const selectThread = useCallback(async (thread) => {
    setCurrentThread(thread);
    
    // Update selected brand to match thread's brand
    if (thread.brand_id && thread.brand_id !== selectedBrand?.brand_id) {
      const matchingBrand = brands.find(b => b.brand_id === thread.brand_id);
      if (matchingBrand) {
        setSelectedBrand(matchingBrand);
      }
    }
    
    try {
      const response = await axios.get(`${API}/api/threads/${thread.thread_id}/messages`, {
        withCredentials: true,
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
    }
  }, [selectedBrand, brands]);

  const deleteThread = useCallback(async (threadId) => {
    try {
      await axios.delete(`${API}/api/threads/${threadId}`, {
        withCredentials: true,
      });
      setThreads((prev) => prev.filter((t) => t.thread_id !== threadId));
      if (currentThread?.thread_id === threadId) {
        setCurrentThread(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  }, [currentThread]);

  const renameThread = useCallback(async (threadId, newTitle) => {
    try {
      await axios.patch(`${API}/api/threads/${threadId}`, {
        title: newTitle,
      }, {
        withCredentials: true,
      });
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === threadId ? { ...t, title: newTitle } : t
        )
      );
      if (currentThread?.thread_id === threadId) {
        setCurrentThread((prev) => ({ ...prev, title: newTitle }));
      }
    } catch (error) {
      console.error('Failed to rename thread:', error);
    }
  }, [currentThread]);

  const sendMessage = useCallback(async (content, threadOverride = null) => {
    // Allow overriding currentThread for cases where state hasn't updated yet
    const thread = threadOverride || currentThread;
    if (!thread || !content.trim()) return;

    // Add user message immediately
    const userMessage = {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // Send message to backend
      await axios.post(`${API}/api/threads/${thread.thread_id}/messages`, {
        content,
      }, {
        withCredentials: true,
      });

      // Start streaming response
      setIsStreaming(true);
      const controller = new AbortController();
      setAbortController(controller);

      // Add placeholder assistant message
      const assistantMessage = {
        message_id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        isStreaming: true,
        toolCalls: [],
        analytics: null,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Stream response using fetch for SSE
      const response = await fetch(`${API}/api/threads/${thread.thread_id}/stream`, {
        credentials: 'include',
        signal: controller.signal,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';  // Accumulate full content

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'content') {
                fullContent += data.content;
                const currentContent = fullContent;  // Capture for closure
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg.role === 'assistant') {
                    lastMsg.content = currentContent;
                  }
                  return [...newMessages];  // Return new array to trigger re-render
                });
              } else if (data.type === 'tool_call') {
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg.role === 'assistant') {
                    lastMsg.toolCalls = [...(lastMsg.toolCalls || []), data];
                  }
                  return newMessages;
                });
              } else if (data.type === 'analytics') {
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg.role === 'assistant') {
                    lastMsg.analytics = data.data;
                  }
                  return newMessages;
                });
              } else if (data.type === 'done') {
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg.role === 'assistant') {
                    lastMsg.isStreaming = false;
                    lastMsg.message_id = data.message_id;
                  }
                  return newMessages;
                });
              } else if (data.error) {
                setMessages((prev) => {
                  const newMessages = [...prev];
                  const lastMsg = newMessages[newMessages.length - 1];
                  if (lastMsg.role === 'assistant') {
                    lastMsg.content = `Error: ${data.error}`;
                    lastMsg.isStreaming = false;
                  }
                  return newMessages;
                });
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to send message:', error);
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMsg = newMessages[newMessages.length - 1];
          if (lastMsg.role === 'assistant') {
            lastMsg.content = 'Sorry, an error occurred. Please try again.';
            lastMsg.isStreaming = false;
          }
          return newMessages;
        });
      }
    } finally {
      setIsStreaming(false);
      setAbortController(null);
    }
  }, [currentThread]);

  const stopGeneration = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setIsStreaming(false);
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg.role === 'assistant' && lastMsg.isStreaming) {
          lastMsg.isStreaming = false;
          lastMsg.content += '\n\n[Generation stopped]';
        }
        return newMessages;
      });
    }
  }, [abortController]);

  return (
    <ChatContext.Provider
      value={{
        threads,
        currentThread,
        messages,
        selectedBrand,
        brands,
        isLoading,
        isStreaming,
        setSelectedBrand,
        selectBrand,
        loadBrands,
        refreshBrands: loadBrands,  // Alias for brand management
        loadThreads,
        createThread,
        selectThread,
        deleteThread,
        renameThread,
        sendMessage,
        stopGeneration,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
